import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarClock, Film, Image as ImageIcon } from "lucide-react";
import { http, fileUrl } from "../lib/api";

const DAY = 86400000;
const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const sameDay = (a, b) => a.toDateString() === b.toDateString();

const Entry = ({ item, onChange }) => {
  const publishNow = async () => {
    await http.patch(`/admin/media/${item.id}`, { publish_at: "" }).then(() => { toast.success("Publicado agora"); onChange(); }).catch(() => toast.error("Falha ao publicar"));
  };
  const time = new Date(item.publish_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="group border border-white/5 bg-[#0d0c11] p-2" data-testid={`calendar-entry-${item.id}`}>
      <div className="relative mb-2 aspect-video overflow-hidden bg-white/5">
        {(item.has_thumb || item.type === "photo") && <img src={fileUrl(item.id, item.has_thumb ? "thumb" : "file")} alt="" className="h-full w-full object-cover" />}
        <span className="font-mono-d absolute left-1 top-1 bg-black/75 px-1.5 py-0.5 text-[10px] text-sky-300">{time}</span>
      </div>
      <p className="flex items-center gap-1 truncate text-xs text-white">
        {item.type === "video" ? <Film size={11} className="shrink-0 text-rose-400" /> : <ImageIcon size={11} className="shrink-0 text-rose-400" />}
        <span className="truncate">{item.title}</span>
      </p>
      <button onClick={publishNow} className="mt-1 text-[10px] uppercase tracking-widest text-sky-300 hover:text-white" data-testid={`calendar-publish-${item.id}`}>Publicar agora</button>
    </div>
  );
};

export const ScheduleCalendar = ({ items, onChange }) => {
  const [week, setWeek] = useState(() => startOfWeek(new Date()));
  const now = new Date();
  const scheduled = useMemo(
    () => items.filter((i) => i.publish_at && new Date(i.publish_at) > now).sort((a, b) => a.publish_at.localeCompare(b.publish_at)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );
  const days = Array.from({ length: 7 }, (_, i) => new Date(week.getTime() + i * DAY));
  const end = new Date(week.getTime() + 7 * DAY);
  const inWeek = scheduled.filter((i) => { const d = new Date(i.publish_at); return d >= week && d < end; });
  const label = `${days[0].toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}`;
  const next = scheduled[0];

  return (
    <div data-testid="schedule-calendar">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif-d flex items-center gap-3 text-3xl text-white"><CalendarClock className="text-rose-500" /> Agenda de <span className="italic text-rose-500">lançamentos</span></h2>
          <p className="mt-2 text-sm text-slate-300" data-testid="calendar-summary">
            {scheduled.length} agendado(s) no total · {inWeek.length} nesta semana
            {next && <> · próximo: <span className="text-white">{new Date(next.publish_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeek(new Date(week.getTime() - 7 * DAY))} className="border border-slate-700 p-2 text-slate-300 hover:border-rose-500 hover:text-white" aria-label="Semana anterior" data-testid="calendar-prev"><ChevronLeft size={16} /></button>
          <button onClick={() => setWeek(startOfWeek(new Date()))} className="border border-slate-700 px-3 py-2 text-xs uppercase tracking-widest text-slate-300 hover:border-rose-500 hover:text-white" data-testid="calendar-today">Hoje</button>
          <button onClick={() => setWeek(new Date(week.getTime() + 7 * DAY))} className="border border-slate-700 p-2 text-slate-300 hover:border-rose-500 hover:text-white" aria-label="Próxima semana" data-testid="calendar-next"><ChevronRight size={16} /></button>
          <span className="font-mono-d ml-2 text-xs text-slate-300" data-testid="calendar-week-label">{label}</span>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-7">
        {days.map((d) => {
          const list = inWeek.filter((i) => sameDay(new Date(i.publish_at), d));
          const today = sameDay(d, now);
          return (
            <div key={d.toISOString()} className={`border p-2 md:min-h-[140px] ${today ? "border-rose-600/70 bg-rose-950/20" : "border-white/5"}`} data-testid={`calendar-day-${d.toISOString().slice(0, 10)}`}>
              <p className={`font-mono-d flex items-baseline justify-between text-xs uppercase tracking-widest ${list.length ? "mb-2" : "md:mb-2"} ${today ? "text-rose-400" : "text-slate-400"}`}>
                <span>{d.toLocaleDateString("pt-BR", { weekday: "short" })}</span>
                <span className="font-serif-d text-lg normal-case tracking-normal text-white">{d.getDate()}</span>
              </p>
              <div className="space-y-2">
                {list.length === 0 ? <p className="text-[11px] text-slate-400 md:hidden">Nada agendado</p> : list.map((i) => <Entry key={i.id} item={i} onChange={onChange} />)}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-6 text-xs text-slate-400">Para mudar a data, use o lápis na aba Gerenciar. Horários no seu fuso.</p>
    </div>
  );
};
